import React, { useEffect, useState } from 'react'
import { AdCreative } from '@marketing-milk/interfaces'
import { SurfacePalette } from '../../tokens'
import { useAdCreativeReview } from 'hooks/api/useAdCreativeReview'
import { ClientApprovalModal } from '../../components/ClientApprovalModal'
import { ClientApprovalRemoveConfirm } from '../../components/ClientApprovalRemoveConfirm'

/**
 * Shared creative-approval interaction for the Edit (`CreativeSection`) and
 * Present (`PresentCreativeSection`) creative grids. Owns the approve/remove
 * modal state and returns the card callbacks plus the modals to render. The
 * per-creative mutation hook is only instantiated while a modal is open.
 *
 * `onModalOpenChange` lets a host suppress side effects while a modal is open —
 * Present passes it to lock slide navigation so Arrow/Esc don't fire underneath
 * the dialog (or discard the presentation) while the AE is recording approval.
 */
export function useCreativeApproval(
  businessID: number,
  palette: SurfacePalette,
  onModalOpenChange?: (open: boolean) => void
) {
  const [approving, setApproving] = useState<AdCreative | undefined>(undefined)
  const [removing, setRemoving] = useState<AdCreative | undefined>(undefined)
  const open = !!approving || !!removing

  useEffect(() => {
    onModalOpenChange?.(open)
    // Release the lock if this host unmounts while a modal is still open, so a
    // Present slide change can never leave deck navigation permanently suppressed.
    return () => onModalOpenChange?.(false)
  }, [open, onModalOpenChange])

  const modals = (
    <>
      {approving && (
        <CreativeApprovalModalHost
          businessID={businessID}
          palette={palette}
          creative={approving}
          onClose={() => setApproving(undefined)}
        />
      )}
      {removing && (
        <CreativeRemoveApprovalHost
          businessID={businessID}
          palette={palette}
          creative={removing}
          onClose={() => setRemoving(undefined)}
        />
      )}
    </>
  )

  return { onApprove: setApproving, onRemoveApproval: setRemoving, modals }
}

const CreativeApprovalModalHost = ({
  businessID,
  palette,
  creative,
  onClose,
}: {
  businessID: number
  palette: SurfacePalette
  creative: AdCreative
  onClose: () => void
}) => {
  const { approveOnBehalfOfClient } = useAdCreativeReview(businessID, creative.id)

  return (
    <ClientApprovalModal
      palette={palette}
      creativeName={creative.name}
      onApprove={async input => {
        await approveOnBehalfOfClient.mutateAsync(input)
      }}
      onClose={onClose}
    />
  )
}

const CreativeRemoveApprovalHost = ({
  businessID,
  palette,
  creative,
  onClose,
}: {
  businessID: number
  palette: SurfacePalette
  creative: AdCreative
  onClose: () => void
}) => {
  const { removeApproval } = useAdCreativeReview(businessID, creative.id)

  return (
    <ClientApprovalRemoveConfirm
      palette={palette}
      creativeName={creative.name}
      onConfirm={async () => {
        await removeApproval.mutateAsync()
      }}
      onClose={onClose}
    />
  )
}
